import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with no default Content-Type
const api = axios.create({
  baseURL: API_BASE,
});

// Add auth token and Content-Type to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Set Content-Type based on request type
  // - GET/DELETE/HEAD/OPTIONS: No Content-Type needed
  // - FormData (file uploads): Let browser set multipart/form-data with boundary automatically
  // - Other POST/PUT/PATCH: Use application/json
  if (['get', 'delete', 'head', 'options'].includes(config.method?.toLowerCase())) {
    delete config.headers['Content-Type'];
  } else if (config.data instanceof FormData) {
    // CRITICAL: Do NOT set Content-Type for FormData - browser must set it with boundary
    delete config.headers['Content-Type'];
  } else {
    config.headers['Content-Type'] = 'application/json';
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data),
  selfRegister: (data) => api.post('/auth/self-register', data),
  getMe: () => api.get('/auth/me'),
};

// User Management APIs
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getPending: () => api.get('/users/pending'),
  create: (data) => api.post('/users', data),
  approve: (userId, data) => api.post(`/users/${userId}/approve`, null, { params: data }),
  reject: (userId) => api.post(`/users/${userId}/reject`),
  update: (userId, data) => api.put(`/users/${userId}`, data),
  resetPassword: (userId, newPassword) => api.post(`/users/${userId}/reset-password`, { new_password: newPassword }),
  delete: (userId) => api.delete(`/users/${userId}`),
};

// Schools APIs
export const schoolsAPI = {
  getAll: () => api.get('/schools'),
  getById: (id) => api.get(`/schools/${id}`),
  create: (data) => api.post('/schools', data),
  update: (id, data) => api.put(`/schools/${id}`, data),
  delete: (id) => api.delete(`/schools/${id}`),
};

// Teachers APIs
export const teachersAPI = {
  getAll: (params) => api.get('/teachers', { params }),
  create: (data) => api.post('/teachers', data),
  update: (id, data) => api.put(`/teachers/${id}`, data),
  delete: (id) => api.delete(`/teachers/${id}`),
};

// Students APIs
export const studentsAPI = {
  getAll: (params) => api.get('/students', { params }),
  getById: (id) => api.get(`/students/${id}`),
  create: (data) => api.post('/students', data),
  update: (id, data) => api.put(`/students/${id}`, data),
  delete: (id) => api.delete(`/students/${id}`),
  getCertificate: (id) => api.get(`/students/${id}/certificate`, { responseType: 'blob' }),
};

// Coaches APIs
export const coachesAPI = {
  getAll: (params) => api.get('/coaches', { params }),
  getById: (id) => api.get(`/coaches/${id}`),
  create: (data) => api.post('/coaches', data),
  update: (id, data) => api.put(`/coaches/${id}`, data),
  delete: (id) => api.delete(`/coaches/${id}`),
};

// Programs APIs
export const programsAPI = {
  getAll: () => api.get('/programs'),
  getById: (id) => api.get(`/programs/${id}`),
  create: (data) => api.post('/programs', data),
  update: (id, data) => api.put(`/programs/${id}`, data),
  delete: (id) => api.delete(`/programs/${id}`),
};

// Locations APIs
export const locationsAPI = {
  getAll: (params) => api.get('/locations', { params }),
  getById: (id) => api.get(`/locations/${id}`),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
};

// Lessons APIs
export const lessonsAPI = {
  getAll: (params) => api.get('/lessons', { params }),
  getById: (id) => api.get(`/lessons/${id}`),
  getByStudent: (studentId) => api.get('/lessons', { params: { student_id: studentId } }),
  create: (data) => api.post('/lessons', data),
  update: (id, data) => api.put(`/lessons/${id}`, data),
  updateStatus: (id, data) => api.put(`/lessons/${id}`, data),
  complete: (id, notes) => api.post(`/lessons/${id}/complete`, null, { params: { notes } }),
  completeWithNotification: (id, notes, sendNotification = true) => 
    api.post(`/lessons/${id}/complete-with-notification`, null, { params: { notes, send_notification: sendNotification } }),
  cancel: (id, reason) => api.post(`/lessons/${id}/cancel`, null, { params: { reason } }),
  restore: (id) => api.post(`/lessons/${id}/restore`),
  delete: (id) => api.delete(`/lessons/${id}`),
  createBulk: (data) => api.post('/lessons/bulk', data),
  bulkCreate: (data) => api.post('/lessons/bulk', data),
};

// Enrollments APIs (Phase 1-4)
export const enrollmentsAPI = {
  getAll: (params) => api.get('/enrollments', { params }),
  getById: (id) => api.get(`/enrollments/${id}`),
  getByStudent: (studentId) => api.get(`/students/${studentId}/enrollments`),
  create: (data) => api.post('/enrollments', data),
  update: (id, data) => api.put(`/enrollments/${id}`, data),
  switchActive: (studentId, enrollmentId) => api.put(`/students/${studentId}/switch-enrollment/${enrollmentId}`),
};

// Certificate APIs
export const certificatesAPI = {
  getAll: (params) => api.get('/certificates', { params }),
  getStatus: (studentId, enrollmentId) => api.get(`/students/${studentId}/certificate-status`, { 
    params: enrollmentId ? { enrollment_id: enrollmentId } : {} 
  }),
  checkEligibility: (studentId, enrollmentId) => api.get(`/students/${studentId}/certificate-status`, { 
    params: enrollmentId ? { enrollment_id: enrollmentId } : {} 
  }),
  approve: (studentId, data = { tier: 'bronze', school_id: null }) => 
    api.post(`/students/${studentId}/certificate/approve`, data),
  revoke: (studentId) => api.post(`/students/${studentId}/certificate/revoke`),
  download: (studentId) => api.get(`/students/${studentId}/certificate/download`, { 
    responseType: 'blob',
    timeout: 30000, // 30 second timeout for PDF generation
  }),
  downloadPDF: (studentId) => api.get(`/students/${studentId}/certificate/download`, { 
    responseType: 'blob',
    timeout: 30000,
  }),
  // Get download URL for mobile fallback (opens in new tab)
  getDownloadUrl: (studentId) => {
    const token = localStorage.getItem('token');
    return `${api.defaults.baseURL}/students/${studentId}/certificate/download?token=${token}`;
  },
};

// Calendar APIs
export const calendarAPI = {
  getLessons: (params) => api.get('/calendar/lessons', { params }),
  rescheduleLesson: (lessonId, newDate, newCoachId, newLocationId) => 
    api.put(`/calendar/lessons/${lessonId}/reschedule`, null, { 
      params: { new_date: newDate, new_coach_id: newCoachId, new_location_id: newLocationId } 
    }),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Activity Logs APIs
export const activityLogsAPI = {
  getAll: (params) => api.get('/activity-logs', { params }),
};

// Notifications APIs
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Email Notifications APIs
export const emailNotificationsAPI = {
  send: (data) => api.post('/notifications/email/send', data),
  sendLessonReminder: (lessonId) => api.post(`/notifications/email/lesson-reminder/${lessonId}`),
  getAll: (params) => api.get('/notifications/email', { params }),
};

// Settings APIs
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Reports APIs
export const reportsAPI = {
  exportStudentsCSV: () => api.get('/reports/students/csv', { responseType: 'blob' }),
  exportStudentsPDF: () => api.get('/reports/students/pdf', { responseType: 'blob' }),
  exportLessonsCSV: () => api.get('/reports/lessons/csv', { responseType: 'blob' }),
};

// Upload APIs
export const uploadAPI = {
  uploadPhoto: (file, entityType, entityId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', entityType);
    formData.append('entity_id', entityId);
    // Do NOT set Content-Type - let browser set multipart/form-data with boundary
    return api.post('/upload/photo', formData);
  },
  // New centralized media upload endpoint - stores in persistent GridFS
  uploadMedia: (file, mediaType = 'general', itemId = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('media_type', mediaType);
    formData.append('item_id', itemId);
    // Do NOT set Content-Type - let browser set multipart/form-data with boundary
    return api.post('/media/upload', formData);
  },
};

// Parent Portal APIs
export const parentAPI = {
  register: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => formData.append(key, data[key]));
    // Do NOT set Content-Type - let browser set multipart/form-data with boundary
    return api.post('/parent/register', formData);
  },
  getChildren: () => api.get('/parent/children'),
  getChildLessons: (studentId) => api.get(`/parent/child/${studentId}/lessons`),
};

// Seed data
export const seedData = () => api.post('/seed-data');

// Landing Page Config APIs (Public + Admin)
export const landingAPI = {
  getConfig: () => api.get('/landing/config'),
  updateConfig: (data) => api.put('/landing/config', data),
  updateProgram: (programId, data) => api.put(`/landing/programs/${programId}`, data),
  updateLocation: (locationId, data) => api.put(`/landing/locations/${locationId}`, data),
  addTestimonial: (data) => api.post('/landing/testimonials', data),
  updateTestimonial: (testimonialId, data) => api.put(`/landing/testimonials/${testimonialId}`, data),
  deleteTestimonial: (testimonialId) => api.delete(`/landing/testimonials/${testimonialId}`),
  updateFeature: (featureId, data) => api.put(`/landing/features/${featureId}`, data),
  uploadImage: (file, imageType, itemId = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('image_type', imageType);
    formData.append('item_id', itemId);
    // Do NOT set Content-Type - let browser set multipart/form-data with boundary
    return api.post('/landing/upload', formData);
  },
};

// Schools Program APIs
export const schoolProgramAPI = {
  // Public endpoints
  getConfig: () => api.get('/school/config'),
  register: (data) => api.post('/school/register', data),
  
  // Admin endpoints
  updateConfig: (data) => api.put('/school/config', data),
  getRegistrations: (params) => api.get('/school/registrations', { params }),
  getRegistration: (id) => api.get(`/school/registrations/${id}`),
  updateRegistration: (id, data) => api.put(`/school/registrations/${id}`, data),
  deleteRegistration: (id) => api.delete(`/school/registrations/${id}`),
  exportCSV: (params) => api.get('/school/registrations/export/csv', { 
    params,
    responseType: 'blob' 
  }),
};

// Media Admin APIs (for migrations and management)
export const mediaAPI = {
  // Trigger bulk migration of local files to GridFS (admin only)
  migrateAll: () => api.post('/media/migrate-all'),
};

// Admin APIs
export const adminAPI = {
  // Legacy migration endpoints (Phase 5)
  getMigrationStatus: () => api.get('/admin/migration-status'),
  runLegacyMigration: () => api.post('/admin/migrate-legacy'),
};

// Lesson Change Request APIs (Safe Lesson Control)
export const lessonRequestsAPI = {
  // Create a change request (Coach or Admin)
  create: (data) => api.post('/lesson-requests', data),
  // Get all requests (Admin sees all, Coach sees own)
  getAll: (params) => api.get('/lesson-requests', { params }),
  // Get single request
  getById: (id) => api.get(`/lesson-requests/${id}`),
  // Get requests for a specific student
  getByStudent: (studentId, params) => api.get(`/students/${studentId}/lesson-requests`, { params }),
  // Admin: Approve request
  approve: (id, notes) => api.put(`/lesson-requests/${id}/approve`, null, { params: { notes } }),
  // Admin: Reject request
  reject: (id, reason) => api.put(`/lesson-requests/${id}/reject`, null, { params: { reason } }),
};

// Enrollment APIs
export const enrollmentAPI = {
  create: (data) => api.post('/enrollments', data),
  getAll: (params) => api.get('/enrollments', { params }),
  getById: (id) => api.get(`/enrollments/${id}`),
  approve: (id) => api.post(`/enrollments/${id}/approve`),
  reject: (id, reason) => api.post(`/enrollments/${id}/reject`, null, { params: { reason } }),
};

export default api;
